import {
  CuriosityAutocompleteProps,
  CuriosityNamespaceType,
  CuriosityAutocomplete,
  CuriosityNamespace,
  CuriosityAutocompleteValueWithNull,
  isCuriosityTranslation,
  CuriosityTranslation,
} from '@procureai/curiosity-ui-mui';
import { forwardRef, ForwardedRef } from 'react';
import { useTranslation } from 'react-i18next';

import { SupplierContactEntity, SupplierContactHitId } from './SupplierContactAutocomplete.types';

const createStaticHit = (
  id: SupplierContactHitId,
  value: string,
  key?: string,
): CuriosityTranslation<SupplierContactEntity> => ({
  id,
  key: key ?? id,
  value: value,
  language: '',
  entity: {
    firstName: '',
    lastName: '',
    preferredLanguage: '',
    sourceReference: '',
    email: '',
    roles: [],
  },
});

export type SupplierContactAutocompleteProps<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  Ns extends CuriosityNamespace = CuriosityNamespace,
> = Omit<CuriosityAutocompleteProps<SupplierContactEntity, Multiple, DisableClearable, Ns>, 'ns'>;

const SupplierContactAutocompleteInner = <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  Ns extends CuriosityNamespace = CuriosityNamespace,
>(
  { ...props }: SupplierContactAutocompleteProps<Multiple, DisableClearable, Ns>,
  ref: ForwardedRef<unknown>,
) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'SupplierContactAutocomplete' });

  return (
    <CuriosityAutocomplete<SupplierContactEntity, Multiple, DisableClearable>
      ref={ref}
      ns={CuriosityNamespaceType.SUPPLIER_CONTACT}
      optionsAfter={({ inputValue }) => [
        createStaticHit(
          SupplierContactHitId.NEW,
          inputValue ? t(SupplierContactHitId.NEW, { query: inputValue }) : t('manual'),
        ),
      ]}
      translateSentinel={(value) => {
        if (value === SupplierContactHitId.NEW) {
          return createStaticHit(SupplierContactHitId.NEW, t('manual'));
        }
      }}
      {...props}
      onChange={(val) => {
        if (props.onChange) {
          const nextArr: CuriosityAutocompleteValueWithNull<SupplierContactEntity> = props.multiple
            ? (val as any)
            : [val];
          const mapped = nextArr.map((changedValue) => {
            if (changedValue && isCuriosityTranslation(changedValue) && changedValue.id === SupplierContactHitId.NEW) {
              return changedValue.key;
            }
            return changedValue;
          });
          props.onChange(props.multiple ? (mapped as any) : mapped[0]);
        }
      }}
    />
  );
};

export const SupplierContactAutocomplete = forwardRef(SupplierContactAutocompleteInner) as <
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  Ns extends CuriosityNamespace = CuriosityNamespace,
>(
  props: SupplierContactAutocompleteProps<Multiple, DisableClearable, Ns> & {
    ref?: ForwardedRef<unknown>;
  },
) => ReturnType<typeof SupplierContactAutocompleteInner>;
